import React, {FC, useRef, useCallback} from 'react';
import cn from 'classnames';
import {Preferences} from '@/app/interfaces/preferences';
import {useToggleState} from '@/common/helpers/hooks/useToggleState';
import {useOutsideClick} from '@/common/helpers/hooks/useOutsideClick';
import styles from './SettingsRow.css';

interface SettingsRowProps {
	field: keyof Preferences;
	title: string;
	value: string;
	options: string[];
	optionNameTransformer(option: string): string;
	onChange(field: keyof Preferences, value: string): void;
}

export const SettingsRow: FC<SettingsRowProps> = props => {
	const {field, title, value, options, optionNameTransformer, onChange} = props;

	const [expanded, switchExpanded] = useToggleState(false);

	const optionsRootRef = useRef<HTMLDivElement>(null);

	useOutsideClick(optionsRootRef, switchExpanded, expanded);

	const handleOptionSelect = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			const index = Number(event.currentTarget.dataset.index!);
			const option = options[index];
			onChange(field, option);
		},
		[field, options, onChange],
	);

	return (
		<div className={styles.root}>
			{title}
			<button className={styles.value} type='button' onClick={switchExpanded}>
				{optionNameTransformer(value)}
			</button>

			{expanded && (
				<div ref={optionsRootRef} className={styles.options}>
					{options.map((option, index) => (
						<button
							key={option}
							className={cn(styles.option, option === value && styles.isCurrent)}
							data-index={index}
							type='button'
							onClick={handleOptionSelect}>
							{optionNameTransformer(option)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

SettingsRow.displayName = 'SettingsRow';
